import React, { useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Select, Table, Tag } from 'antd';

const OrchestraAttendanceTable = ({ groups, performanceGroups, assignments, registrants, getName, matchesRegistrant,
    readOnly, onMembersChange, onAdd, onEdit, onRemove }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const normalizedSearch = searchTerm.trim();
    const performanceByRegistrant = new Map();
    performanceGroups.forEach(group => (assignments[group.id] || []).forEach(registrant => {
        performanceByRegistrant.set(registrant.id, group.label || group.name);
    }));
    const attendanceByRegistrant = new Map();
    const attendees = groups.flatMap(group => (assignments[group.id] || []).map(registrant => {
        attendanceByRegistrant.set(registrant.id, group.label);
        return { key: `${group.id}:${registrant.id}`, registrant, group };
    }));
    // Searching the roster never hides a group editor or changes its complete membership.
    const visibleAttendees = normalizedSearch
        ? attendees.filter(row => matchesRegistrant(row.registrant, normalizedSearch)) : attendees;
    const attendeeColumns = [
        { title: 'Performer / Registrant', key: 'name', render: (_, row) => getName(row.registrant) },
        { title: 'Teacher', key: 'teacher', render: (_, row) => row.registrant.teacherName || '—' },
        { title: 'Performance group', key: 'performance', render: (_, row) =>
            performanceByRegistrant.get(row.registrant.id) || 'No performance assigned' },
        { title: 'Orchestra group', key: 'orchestra', render: (_, row) => (
            <div><strong>{row.group.label}</strong><div>{row.group.venueLabel || row.group.venue} · {row.group.date}</div></div>
        ) },
    ];
    const columns = [
        { title: 'Attendance group', key: 'group', width: 220, render: (_, group) => (
            <div><strong>{group.label}</strong><div>{group.venueLabel || group.venue}</div><div>{group.date}</div></div>
        ) },
        { title: 'Registered attendees', key: 'members', render: (_, group) => {
            const members = assignments[group.id] || [];
            if (readOnly) return members.length ? members.map(registrant => (
                <Tag key={registrant.id}>{getName(registrant)}</Tag>
            )) : 'No attendees assigned';
            const elsewhere = new Set(groups.filter(other => other.id !== group.id)
                .flatMap(other => (assignments[other.id] || []).map(registrant => registrant.id)));
            const available = new Map([...registrants, ...members].map(registrant => [registrant.id, registrant]));
            return <Select mode="multiple" showSearch style={{ width: '100%' }}
                placeholder="Search performer or teacher to add attendees"
                aria-label={`Assign attendees to ${group.label}`}
                filterOption={(input, option) => matchesRegistrant(option.registrant, input)}
                notFoundContent="No registered attendees match this search"
                value={members.map(registrant => registrant.id)}
                onChange={ids => onMembersChange(group.id, ids)}
                options={[...available.values()].map(registrant => ({
                    value: registrant.id,
                    registrant,
                    disabled: elsewhere.has(registrant.id),
                    label: `${getName(registrant)} — ${registrant.eventId || 'APCS2025'} — ${performanceByRegistrant.get(registrant.id) || 'No performance assigned'}${registrant.teacherName ? ` — Teacher: ${registrant.teacherName}` : ''}${elsewhere.has(registrant.id) ? ` — Already assigned: ${attendanceByRegistrant.get(registrant.id)}` : ''}`,
                }))} />;
        } },
        { title: 'Registrations', key: 'count', width: 120, render: (_, group) => (assignments[group.id] || []).length },
    ];
    if (!readOnly) columns.push({ title: 'Actions', key: 'actions', width: 160, render: (_, group) => (
        <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" onClick={() => onEdit(group)}>Edit</Button>
            <Button size="small" danger onClick={() => onRemove(group)}>Delete</Button>
        </div>
    ) });
    return <section className="sam-attendance-section">
            <div className="sam-attendance-header">
                <h3>Orchestra Attendance</h3>
                {!readOnly && <Button onClick={onAdd}>Add orchestra group</Button>}
            </div>
            <p>Search by performer or teacher inside a group's selector to add attendees. Save applies both sections.</p>
            <Table rowKey="id" columns={columns} dataSource={groups} pagination={false}
                scroll={{ x: 850 }} locale={{ emptyText: 'No orchestra attendance groups yet' }} />
            <div className="sam-attendance-header sam-attendance-roster-header">
                <h3>Assigned Registrants</h3>
                <div className="sam-attendance-controls">
                    <Input prefix={<SearchOutlined />} placeholder="Search performer or teacher..."
                        aria-label="Search orchestra attendees by performer or teacher name"
                        value={searchTerm} onChange={event => setSearchTerm(event.target.value)} allowClear />
                </div>
            </div>
            <Table rowKey="key" columns={attendeeColumns} dataSource={visibleAttendees}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: 850 }} locale={{ emptyText: normalizedSearch
                    ? 'No assigned registrants match this search' : 'No registrants assigned to orchestra groups yet' }} />
        </section>;
};

export default OrchestraAttendanceTable;
